import { grey } from '@mui/material/colors'
import { alpha, createTheme } from '@mui/material/styles'
import {
  accordionBaseStyles,
  accordionDetailsStyles,
  accordionSummaryBaseStyles,
  autocompleteBaseStyles,
  breakpointValues,
  cardBaseStyles,
  collapseStyles,
  dataGridCheckboxStyles,
  dataGridVirtualScrollerStyles,
  dialogPaperStyles,
  formControlStyles,
  formLabelStyles,
  gridBaseStyles,
  hiddenSearchColumnStyles,
  inputBaseInputStyles,
  inputLabelOutlinedStyles,
  menuStyles,
  outlinedButtonHoverFill,
  paperBaseStyles,
  quillDarkStyles,
  quillLightStyles,
  selectBaseStyles,
  tableContainerStyles,
  tableStyles,
  themeScrollbarStyles,
  tooltipBaseStyles,
} from '../tokens/components'

// OBxOPs Design System - Core Surface Colors
const paper = '#121826'
const section = '#1A1E2E'
// const section = '#1c2a3a'
// const def = '#1E3456'
const def = '#1c2a3a'

// OBxOPs Accent Colors
const ORANGE = '#E87722'
const OBU_BLUE = '#336699'
const GOLD = '#DAA520'
const FROST = '#C8D6E5'
const GLACIER = '#f7f9fb'
const SLATE = '#384050'
const OBX_DARK_BLUE = '#17212e'
const OBX_CONTRAST = '#141a20'
const BRIGHT_BLUE = '#3498DB'
const CERULEAN = '#4A90D9'
const SUNSET = '#CC6B5A'
const CORAL = '#E05050'
const OBX_TEAL = '#4aa17b'
const LINEAR_BACKGROUND = `linear-gradient(90deg, ${ORANGE}, ${GOLD}, ${SUNSET})`

export const obxopsTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      lighter: FROST,
      main: GOLD,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: OBU_BLUE,
      contrastText: FROST,
    },
    background: {
      image: 'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)',
      divider: SLATE,
      pinnedrows: 'rgba(56, 64, 80, 0.28)',
      pinnedColumms: '#141928',
      default: def,
      defaultdim: 'rgba(18, 24, 38, 0.5)',
      section: 'rgba(18, 24, 38, 0.5)',
      subtasksection: 'rgba(26, 30, 46, 0.8)',
      mentions: section,
      header: paper,
      main: section,
      tooltip: 'rgba(0, 0, 0, 0.87)',
      input: '#222A3E',
      icon: '#2A3248',
      sectionContent: SLATE,
      selected: paper,
      timelineGrid: 'rgba(51, 102, 153, 0.5)',
      help: OBU_BLUE,
      paper,
      disabled: 'rgba(18, 24, 38, 0.3)',
      toolbar: SLATE,
      loading: GOLD,
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
      isDragging: '0 4px 6px rgba(0, 0, 0, 0.3)',
      overlay: 'rgba(18, 24, 38, 0.1)',
      thumbnail: 'rgba(232, 119, 34, 0.8)',
      details: '#283848',
      fielddisabled: '#2A3248',
      scrollbar_thumb: GOLD,
      scrollbar_hover: SLATE,
      dialogtext: def,
      snackbar: 'transparent',
      postdated: 'rgba(218, 165, 32, 0.56)',
      gradientHeader: 'linear-gradient(135deg, rgba(218, 165, 32, 0.08), rgba(91, 141, 239, 0.05))',
      gradientDefault: `linear-gradient(180deg, ${paper} 0%, ${section} 38%, ${def} 100%)`,
      gradientSection: `linear-gradient(180deg, ${section} 0%, ${def} 100%)`,
      gradientPaper: `linear-gradient(180deg, ${paper} 0%, ${section} 100%)`,
      vignette: `radial-gradient(1200px 800px at 50% 0%, ${alpha('#000', 0.0)} 40%, ${alpha('#000', 0.35)} 100%)`,
      button: GOLD,
      ai_accent_bg: 'rgba(232, 119, 34, 0.03)',
      ai_accent_border: 'rgba(232, 119, 34, 0.3)',
      ai_accent_gradient: `linear-gradient(135deg, ${ORANGE}, ${GOLD})`,
      ai_accent_gradient_hover: 'linear-gradient(135deg, #D06A1A, #C4941A)',
    },
    text: {
      primary: 'rgba(255,255,255,1)',
      contrast: 'rgba(0,0,0,1)',
      shadow: 'rgba(0,0,0,0.5)',
      highlight: GOLD,
      ai_agent_text: '#C8A882',
      ai_accent: ORANGE,
      ai_accent_secondary: GOLD,
      complete: '#4AA17B',
      subtask: CERULEAN,
      secondary: GLACIER,
      disabled: 'rgba(155, 165, 176, 0.5)',
      tertiary: 'rgba(200, 214, 229, 0.3)',
      resetFilter: GOLD,
      chip: 'rgba(0,0,0,1)',
      admin: 'rgba(255, 123, 187, 1)',
      success: 'rgb(80, 200, 120)',
      successdim: 'rgba(129, 230, 163, 1)',
      warning: GOLD,
      dirty: 'rgba(51, 102, 153, 0.3)',
      error: CORAL,
      errordim: 'rgba(224, 80, 80, 0.5)',
      link: BRIGHT_BLUE,
      revise: SUNSET,
      review: CERULEAN,
      htmlText: 'rgba(255,255,255,1)',
      parent: CERULEAN,
      size: 12,
    },
    badge: {
      dot: GOLD,
    },
    chip: {
      sizeSmall: { height: 15, width: 15 },
    },
    card: {
      default: paper,
    },
    border: {
      top: SUNSET,
    },
    gold: {
      main: GOLD,
      dark: '#B8891A',
      contrastText: '#FFFFFF',
    },
    orange: {
      main: ORANGE,
      dark: '#C46318',
      contrastText: '#FFFFFF',
    },
    blueCta: {
      main: BRIGHT_BLUE,
      dark: '#2980B9',
      contrastText: '#FFFFFF',
    },
    danger: {
      main: CORAL,
      dark: '#C43A3A',
      contrastText: '#FFFFFF',
    },
    ghost: {
      main: GLACIER,
      dark: '#7A848E',
      contrastText: '#FFFFFF',
    },
    icon: {
      complete: 'rgb(5, 190, 113)',
      warning: GOLD,
      decline: 'rgba(220, 20, 60, 0.8)',
      deals: '#fbc11e',
      teams: 'rgb(99, 102, 241)',
      organizations: 'rgb(114, 229, 218)',
      approvals: 'rgba(205, 254, 194, 1)',
      contacts: 'rgb(255, 123, 187)',
      nco: 'rgb(52, 211, 145)',
      accounting: BRIGHT_BLUE,
      teamtasks: 'rgb(247, 63, 236)',
      website: 'rgb(84, 126, 240)',
      creative: 'rgb(150, 88, 238)',
      seo: 'rgb(238, 111, 141)',
      mtb: 'rgb(20, 119, 248)',
      products: 'rgb(224, 110, 215)',
      campaignChecklist: GOLD,
      projects: GOLD,
      insight: 'rgb(65, 190, 169)',
      observation: 'rgb(168, 202, 255)',
      leasingPercentage: 'rgba(230, 181, 21, 1)',
      pandadoc: 'rgb(36, 133, 103)',
      dashboard: 'rgb(5, 190, 113)',
      roadmap_icon: '#4aa17b',
      roadmap_icon_bg: '#1f3439',
    },
    list: {
      selected: 'rgba(232, 119, 34, 0.6)',
      focused: 'rgba(51, 102, 153, 0.6)',
    },
    tertiary: {
      lighter: grey[50],
      light: grey[100],
      main: grey[400],
      dark: grey[700],
      darker: grey[900],
    },
    myStyledComponentBackground: '#0D1117',
    action: {
      selected: alpha(GOLD, 0.16),
      selectedOpacity: 0.16,
      hoverOpacity: 0.08,
    },
  },
  typography: {
    fontFamily: '"Roboto", sans-serif',
    fontSize: 14,
    h1: {
      fontSize: '2.5rem',
    },
  },
  spacing: 8,
  hover: SLATE,
  breakpoints: {
    values: breakpointValues,
  },
  shadows: Array(25)
    .fill('none')
    .map((_, index) =>
      index === 0
        ? 'none'
        : `0px ${index}px ${index * 2}px rgba(0, 0, 0, 0.2),
    0px ${index / 2}px ${index}px rgba(0, 0, 0, 0.16),
    0px ${index}px ${index * 2}px rgba(0, 0, 0, 0.24)`
    ) as any,

  components: {
    MyCustomQuill: {
      light: quillLightStyles,
      dark: quillDarkStyles,
    },
    MuiCssBaseline: {
      styleOverrides: theme => ({
        body: {
          backgroundImage: [
            theme.palette.background.image,
            theme.palette.background.vignette,
            theme.palette.background.gradientDefault,
          ].join(','),
          backgroundColor: theme.palette.background.default,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        },
        '@media (prefers-reduced-motion: reduce)': {
          body: {
            backgroundAttachment: 'scroll, scroll, scroll',
          },
        },
        root: {
          ...themeScrollbarStyles(theme),
        },
      }),
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...paperBaseStyles(theme),
          backgroundImage: theme.palette.background.gradientPaper,
        }),
      },
    },
    MuiCollapse: {
      styleOverrides: collapseStyles,
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: alpha(theme.palette.text.primary, 0.08),
          },
          '&.Mui-selected': {
            color: theme.palette.gold.main,
            backgroundColor: `${alpha(theme.palette.background.defaultdim, 0.45)} !important`,
            fontWeight: 'bold',
          },
          '&.MuiTab-textColorPrimary.Mui-disabled': {
            color: `${alpha(theme.palette.text.primary, 0.4)} !important`,
          },
        }),
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          ...outlinedButtonHoverFill(theme),
        }),
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiTable: {
      styleOverrides: tableStyles,
    },
    MuiTableContainer: {
      styleOverrides: tableContainerStyles,
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: paper,
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {},
      },
    },
    MuiMenu: {
      styleOverrides: menuStyles,
    },
    MuiMenuList: {
      styleOverrides: {
        root: {
          backgroundColor: paper,
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...themeScrollbarStyles(theme),
        }),
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...themeScrollbarStyles(theme),
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'rgb(255,255,255,0.5)',
          '&.Mui-focused': {
            color: GOLD,
          },
        },
        outlined: inputLabelOutlinedStyles,
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        option: ({ theme }) => ({
          height: 'auto',
          backgroundColor: paper,
          '&.Mui-focused .MuiListItemButton-root, &.Mui-focusVisible .MuiListItemButton-root': {
            backgroundColor: theme.palette.background.toolbar,
          },
          '&[aria-selected="true"] .MuiListItemButton-root': {
            backgroundColor: theme.palette.background.help,
          },
          '&:hover': {
            backgroundColor: section,
            cursor: 'pointer',
          },
        }),
        input: {
          ...autocompleteBaseStyles.input,
          color: 'rgba(255,255,255,1)',
        },
        root: autocompleteBaseStyles.root,
        popper: autocompleteBaseStyles.popper,
        paper: {
          ...autocompleteBaseStyles.paper,
        },
        inputRoot: {
          '& .MuiOutlinedInput-root': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: GOLD,
              borderWidth: '1px',
              padding: '0px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: GOLD,
              borderWidth: '2px',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'white',
              borderWidth: '2px',
            },
          },
        },
        listbox: ({ theme }) => themeScrollbarStyles(theme),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        body: {
          textAlign: 'center',
          margin: '5px',
          wordWrap: 'break-word',
          whiteSpace: 'normal',
          textOverflow: 'hidden',
          height: '2px',
          lineHeight: '1.25',
          fontSize: '14px',
          justifyContent: 'center',
          alignItems: 'center',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {},
        input: {
          ...inputBaseInputStyles,
          color: 'white',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: formControlStyles,
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: OBX_CONTRAST,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: GOLD,
            borderWidth: '1px',
          },
        }),
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          '& > *': {
            transition: 'transform 0.3s ease',
          },
          '&:hover > *': {
            transform: 'scale(1.07)',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          ...formLabelStyles.root,
          '&.Mui-focused': {
            color: GOLD,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {},
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          ...selectBaseStyles,
          '&.Mui-disabled': {},
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-selected': {
            backgroundColor: `${alpha(theme.palette.background.help, 0.25)} !important`,
          },
          '&.Mui-selected:hover': {
            backgroundColor: `${alpha(theme.palette.background.section, 0.2)} !important`,
          },
        }),
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          ...accordionBaseStyles,
          backgroundColor: section,
          color: FROST,
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        ...accordionSummaryBaseStyles,
        root: {
          ...accordionSummaryBaseStyles.root,
          backgroundColor: section,
          color: 'white',
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          ...accordionDetailsStyles.root,
          backgroundColor: paper,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          ...tooltipBaseStyles,
          backgroundColor: 'rgba(0,0,0,0.92)',
          color: '#FFFFFF',
          border: `1px solid ${GOLD}`,
          boxShadow: `0 4px 12px rgba(0, 0, 0, 0.3)`,
        },
        arrow: {
          color: 'rgba(0,0,0,0.92)',
          '&::before': {
            border: `1px solid ${GOLD}`,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: dialogPaperStyles,
      },
    },
    MuiGrid: {
      styleOverrides: {
        ...gridBaseStyles,
        root: ({ theme }) => ({
          ...gridBaseStyles.root,
          ...themeScrollbarStyles,
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          ...cardBaseStyles,
          backgroundColor: 'transparent',
          color: 'rgba(255,255,255,1)',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...cardBaseStyles,
          backgroundColor: 'transparent',
          color: 'rgba(255,255,255,1)',
          ...themeScrollbarStyles,
        }),
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: 'white',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          borderColor: alpha(theme.palette.text.primary, 0.3),
          fontWeight: 500,
          '& .MuiChip-deleteIconSmall': {
            color: theme.palette.text.primary,
          },
          '&.MuiChip-colorGold': {
            color: theme.palette.gold.main,
            borderColor: theme.palette.gold.main,
          },
          '&.MuiChip-colorOrange': {
            color: theme.palette.orange.main,
            borderColor: theme.palette.orange.main,
          },
          '&.MuiChip-colorDanger': {
            color: theme.palette.danger.main,
            borderColor: theme.palette.danger.main,
          },
        }),
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: 0,
          fontFamily: '"Roboto", sans-serif',
          ...dataGridVirtualScrollerStyles,
          background: 'transparent',
          '& .MuiDataGrid-scrollbar': {
            scrollbarWidth: 'auto',
            scrollbarColor: `${theme.palette.text.primary} ${theme.palette.background.default}`,
          },
          '&': {
            scrollbarColor: `${theme.palette.text.primary} ${theme.palette.background.default}`,
          },
          '& .MuiDataGrid-cell': {
            color: 'rgba(255, 255, 255)',
            '& > *': {
              transition: 'transform 0.3s ease',
            },
            '&:hover > *': {
              transform: 'scale(1.07)',
            },
          },
          '& .MuiDataGrid-columnHeaders': {
            fontSize: '14px',
            backgroundColor: paper,
            color: '#fafafa',
          },
          '& .MuiDataGrid-row': {
            fontSize: '12px',
            boxShadow: `0 1px 0 0 ${theme.palette.text.disabled}`,
            '&:hover': {
              backgroundColor: 'inherit',
            },
          },
          '.MuiDataGrid-row.Mui-selected': {
            '&:hover': {
              backgroundColor: 'rgba(232, 119, 34, 0.2)',
            },
            backgroundColor: 'rgba(232, 119, 34, 0.15)',
          },
          ...dataGridCheckboxStyles,
          '& .MuiDataGrid-pinnedRows--bottom': {
            p: 0,
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
            backgroundColor: def,
            width: '100%',
            minWidth: '100%',
            borderRadius: '0 !important',
            margin: '0 !important',
            borderTop: `2px solid ${theme.palette.divider} !important`,
          },
          '& .MuiDataGrid-filler--pinnedRight': {
            backgroundColor: `transparent !important`,
          },
          '& .MuiDataGrid-filler--pinnedLeft': {
            backgroundColor: `transparent !important`,
          },
          '.MuiDataGrid-footerCell': {
            color: 'rgba(255,255,255,1)',
          },
          '.MuiDataGrid-footerCell:hover': {
            cursor: 'default',
          },
          '& .MuiDataGrid-toolbarContainer': {
            backgroundColor: def,
            overflow: 'visible',
            position: 'relative',
            zIndex: 10,
          },
          '& .MuiDataGrid-toolbarContainer .MuiToolbar-root': {
            overflow: 'visible',
            paddingTop: '12px',
            minHeight: '56px',
          },
          '& .MuiDataGrid-container--top [role=row]': {
            backgroundColor: def,
            borderLeft: '4px solid transparent',
            borderRight: '4px solid transparent',
            borderTop: `4px solid ${section}`,
          },
          // Aggregation row styling
          '& .MuiDataGrid-row[data-id="auto-generated-group-footer-root"]': {
            backgroundColor: `${theme.palette.background.defaultdim} !important`,
          },
          '& .MuiDataGrid-row[data-id="auto-generated-group-footer-root"] .MuiDataGrid-cell': {
            backgroundColor: 'inherit !important',
          },
          '& .MuiDataGrid-row--lastVisible[data-id="auto-generated-group-footer-root"]': {
            zIndex: 100,
            backgroundColor: theme.palette.background.defaultdim,
            width: '100%',
            pointerEvents: 'none',
          },

          // Pinned columns
          '& .MuiDataGrid-pinnedColumns--left, & .MuiDataGrid-pinnedColumns--right': {
            backgroundColor: theme.palette.background.input,
          },
          '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
            backgroundColor: `${theme.palette.background.input} !important`,
            boxShadow: `
                        1px 0 0 0 ${alpha(theme.palette.divider, 0.12)},
                        0 1px 0 0 ${alpha(theme.palette.divider, 0.12)}
                      `,
          },
          '& .MuiDataGrid-cell--pinnedLeft:hover, & .MuiDataGrid-cell--pinnedRight:hover': {
            boxShadow: `
                        1px 0 0 0 ${alpha(theme.palette.divider, 0.12)},
                        0 1px 0 0 ${alpha(theme.palette.divider, 0.12)}
                      `,
          },
          '& .MuiDataGrid-pinnedColumnHeaders--left, & .MuiDataGrid-pinnedColumnHeaders--right': {
            backgroundColor: theme.palette.background.input,
          },
          '& .MuiDataGrid-pinnedColumnHeaders--left .MuiDataGrid-columnHeader, & .MuiDataGrid-pinnedColumnHeaders--right .MuiDataGrid-columnHeader': {
            backgroundColor: `${theme.palette.background.input} !important`,
            boxShadow: `
                        1px 0 0 0 ${alpha(theme.palette.divider, 0.12)},
                        0 1px 0 0 ${alpha(theme.palette.divider, 0.12)}
                      `,
          },
          '& .MuiDataGrid-pinnedColumns .MuiDataGrid-row[data-id="auto-generated-group-footer-root"]': {
            backgroundColor: theme.palette.background.paper,
          },
          '& .MuiDataGrid-virtualScrollerContent, & .MuiDataGrid-virtualScrollerRenderZone': {
            backgroundColor: 'transparent',
          },
          '& .MuiDataGrid-container--bottom': {
            padding: '0 !important',
            margin: '0 !important',
          },
          '& .MuiDataGrid-scrollArea--bottom': {
            padding: '0 !important',
            margin: '0 !important',
          },
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row, & .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-cell': {
            backgroundColor: `${theme.palette.background.paper} !important`,
          },
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row[data-id="__SERVER_AGGREGATIONS_ROW__"]': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            fontWeight: 600,
          },
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row[data-id="__SERVER_AGGREGATIONS_ROW__"] .MuiDataGrid-cell': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            pointerEvents: 'none',
          },
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row[data-id="__SERVER_AGGREGATIONS_ROW__"]:hover': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            cursor: 'default',
          },
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row[data-id="__SERVER_AGGREGATIONS_ROW__"]:hover .MuiDataGrid-cell': {
            backgroundColor: `${theme.palette.background.paper} !important`,
          },
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row[data-id="__SERVER_AGGREGATIONS_ROW__"] .MuiDataGrid-cell:hover': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            cursor: 'default',
          },
          // Row Classes
          '& .high-priority-row': {
            backgroundColor: 'rgba(255, 0, 0, 0.1) !important',
          },
          '& .postdated': {
            backgroundColor: 'rgba(218, 165, 32, 0.15) !important',
          },
          '& .postdated:hover': {
            backgroundColor: 'rgba(218, 165, 32, 0.15) !important',
          },
          '& .postdated .MuiDataGrid-cell': {
            backgroundColor: 'inherit !important',
          },
          '& .postdated .MuiDataGrid-cell:hover': {
            backgroundColor: 'inherit !important',
          },
          '& .MuiDataGrid-row.highlighted-row': {
            opacity: '1 !important',
            backgroundColor: 'rgba(201, 52, 52, 0.2) !important',
            zIndex: '1500 !important',
          },
          '& .MuiDataGrid-row.Mui-selected.MuiDataGrid-row--firstVisible.MuiDataGrid-row--lastVisible.highlighted-row': {
            opacity: '1 !important',
            backgroundColor: 'rgba(228, 22, 22, 0.2) !important',
            zIndex: '1500 !important',
          },
          '& .task-row': {
            backgroundColor: section,
            fontWeight: 600,
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          },
          '& .subtask-row': {
            backgroundColor: theme.palette.background.default,
            paddingLeft: '32px',
          },
          '& .disabled-selection': {
            opacity: '0.3 !important',
            pointerEvents: 'none',
            backgroundColor: 'rgba(100, 100, 100, 0.05) !important',
          },
          '& .product-row': {
            backgroundColor: 'rgba(100, 100, 100, 0.1)',
          },
          '& .MuiDataGrid-row.delete-row': {
            border: '1px solid',
            borderColor: 'rgb(238, 124, 124) !important',
            transition: 'background-color 0.3s ease',
            zIndex: '1900 !important',
            position: 'relative !important',
          },
          // Org Name Header Styling
          '& .org-name-header .MuiDataGrid-columnHeaderTitleContainer': {
            flex: 1,
            minWidth: 0,
          },
          '& .org-name-header .MuiDataGrid-columnHeaderTitleContainerContent': {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            minWidth: 0,
          },
          '& .org-name-header .MuiDataGrid-iconButtonContainer': {
            flexShrink: 0,
          },
          '& .org-name-header .MuiStack-root': {
            display: 'flex',
            flex: '1 1 auto',
            minWidth: 0,
          },
          '& .org-name-header .MuiStack-root > .MuiTypography-root': {
            flex: '1 1 auto',
            minWidth: 0,
          },
          '& .org-name-header .MuiStack-root > .MuiBox-root': {
            marginLeft: 'auto',
            display: 'flex',
            gap: '8px',
            flex: '0 0 auto',
          },
          // Pinned Rows Top Styling
          '& .MuiDataGrid-pinnedRows, & .MuiDataGrid-pinnedRows--top': {
            borderRadius: '0 !important',
            padding: '0 !important',
            margin: '0 !important',
            border: '0 !important',
            width: '100%',
          },

          '& .MuiDataGrid-pinnedRows--top .MuiDataGrid-row, & .MuiDataGrid-pinnedRows--top .MuiDataGrid-cell': {
            borderRadius: '0 !important',
            backgroundColor: theme.palette.background.defaultdim,
          },
          '& .MuiDataGrid-container--top': {
            padding: '0 !important',
            margin: '0 !important',
            border: '0 !important',
            overflow: 'visible',
          },
          '& .MuiDataGrid-container--top [role="row"]': {
            border: '0 !important',
          },
          '& .MuiDataGrid-pinnedRows--top': {
            paddingBottom: '22px',
          },
          '& .MuiDataGrid-pinnedRows--top .MuiDataGrid-cell': {
            paddingTop: '2px',
            paddingBottom: '2px',
          },
          '& .MuiDataGrid-pinnedRows--top .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-groupingCriteriaCellToggle': {
            display: 'none !important',
            pointerEvents: 'none',
          },
          '& .MuiDataGrid-pinnedRows--top .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-groupingCriteriaCell, & .MuiDataGrid-pinnedRows--top .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-cellContent': {
            marginLeft: '0 !important',
            paddingLeft: '0 !important',
          },
          // Pinned Rows Bottom - Additional Styling
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-virtualScrollerRenderZone, & .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-virtualScrollerContent': {
            backgroundColor: `${theme.palette.background.paper} !important`,
          },
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-groupingCriteriaCellToggle': {
            display: 'none !important',
            pointerEvents: 'none',
          },
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-groupingCriteriaCell, & .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-cellContent': {
            marginLeft: '0 !important',
            paddingLeft: '0 !important',
          },
          // Drawer Open State
          '&.has-drawer-open .MuiDataGrid-row': {
            position: 'relative',
            opacity: 0.9,
          },
          '&.has-drawer-open .MuiDataGrid-row:not(.drawer-current-row)::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.35)',
            pointerEvents: 'none',
          },
          '&.has-drawer-open .drawer-current-row': {
            isolation: 'isolate',
            zIndex: 1,
            opacity: '1 !important',
          },
          '&.has-drawer-open .drawer-current-row .MuiDataGrid-cell': {
            backgroundColor: 'rgba(232, 119, 34, 0.2)',
          },
          '&.has-drawer-open .MuiDataGrid-columnHeaders, &.has-drawer-open .MuiDataGrid-toolbarContainer, &.has-drawer-open .MuiDataGrid-footerContainer, &.has-drawer-open .MuiDataGrid-toolbar, &.has-drawer-open .MuiDataGrid-cellCheckbox': {
            opacity: 0.1,
            backgroundColor: 'transparent',
            pointerEvents: 'none',
          },
          '&.has-drawer-open .MuiDataGrid-row:hover': {
            opacity: 1,
            backgroundColor: 'rgba(232, 119, 34, 0.1) !important',
          },
          // Mass Update Highlighting
          '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell.mass-update-target': {
            backgroundColor: 'rgba(232, 119, 34, 0.12)',
            borderLeft: '2px solid rgba(232, 119, 34, 0.4)',
          },
          '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell.mass-update-target:hover': {
            backgroundColor: 'rgba(232, 119, 34, 0.2)',
          },
          '& .MuiDataGrid-cell--editing.mass-update-target': {
            backgroundColor: 'rgba(232, 119, 34, 0.2) !important',
            boxShadow: 'inset 0 0 0 2px rgba(232, 119, 34, 0.5)',
          },
          // NCO Expand Button
          '& .nco-expand-button': {
            pointerEvents: 'var(--nco-expand-events) !important',
            color: 'var(--nco-expand-color) !important',
          },
          ...hiddenSearchColumnStyles,
        }),
        pagination: ({ theme }) => ({
          color: theme.palette.text.secondary,
        }),
        footerContainer: ({ theme }) => ({
          backgroundColor: theme.palette.background.defaultdim,
          '& .MuiTablePagination-root': {
            backgroundColor: 'transparent',
          },
          '& .MuiTablePagination-toolbar': {
            backgroundColor: 'transparent',
          },
          '& .MuiToolbar-root': {
            backgroundColor: 'transparent',
          },
        }),
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {},
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: section,
        },
      },
    },
  },
})
